import { msgid, ngettext, t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { IOptionSingleValue, IProduct } from "../../models/catalogue";
import {
    asMultiValueOption,
    getOptionValueDisplayName,
    isProductPriceDiscounted,
} from "../../utils/catalogue";
import { roundedMoney } from "../../utils/format";
import { notEmpty } from "../../utils/functional";
import { assertNever } from "../../utils/never";
import { intersection } from "../../utils/sets";
import { sortProductOptions } from "../../utils/sorting";
import {
    GridFilterSelectionMode,
    IBooleanAttribute,
    IDualBoundPriceRange,
    IFilterOption,
    IGridFilterOptions,
    IPriceRange,
    ISingleBoundPriceRange,
    PriceComparator,
    parseGridFilters,
} from "./models";

const isSingleBoundRange = (
    range: IPriceRange,
): range is ISingleBoundPriceRange => {
    return range.type === "single_bound_price_range";
};

export abstract class GridFilterConfig {
    public readonly filterID: string;
    public readonly label: string;
    public readonly selectionMode: GridFilterSelectionMode;
    /**
     * Whether the sidebar draws an accordion for this filter.
     *
     * Only the accordion: a hidden filter still reads its value from the URL
     * and still filters, which is what makes it usable by a Quick Filter Pill
     * that has no checkbox of its own.
     *
     * Absent means shown -- grids authored before the field existed do not
     * carry it, and every one of them was visible.
     */
    public readonly showInSidebar: boolean;

    constructor(opts: IGridFilterOptions) {
        this.filterID = opts.filter_id;
        this.label = opts.label;
        this.selectionMode =
            opts.selection_mode === undefined
                ? GridFilterSelectionMode.SELECT_MANY
                : opts.selection_mode;
        this.showInSidebar = opts.show_in_sidebar !== false;
    }

    public abstract listFilterOptions(
        products: ReadonlyArray<IProduct>,
    ): IFilterOption[];

    public abstract applyFilter(
        selectedOptionIDs: Set<string>,
        products: Iterable<IProduct>,
    ): Generator<IProduct>;
}

/**
 * Product Grid Filter which filters based on the values of a single product attribute.
 */
export class AttributeValueGridFilter extends GridFilterConfig {
    public listFilterOptions(
        products: ReadonlyArray<IProduct>,
    ): IFilterOption[] {
        const options = new Set<IOptionSingleValue>();
        const displayNameMap = new Map<string, string>();
        const collectOption = (product: IProduct) => {
            if (!product.availability.is_available_to_buy) {
                return;
            }
            const attr = product.attributes[this.filterID];
            if (
                attr &&
                attr.value &&
                (typeof attr.value === "string" || Array.isArray(attr.value))
            ) {
                const value = asMultiValueOption(attr.value);
                value.forEach((v) => {
                    options.add(v);
                });
                if (attr.display_names && value.length === 1) {
                    displayNameMap.set(
                        value[0],
                        getOptionValueDisplayName({
                            name: attr.name,
                            value: attr.value,
                            display_names: attr.display_names,
                        }),
                    );
                }
            }
        };
        for (const product of products) {
            collectOption(product);
            for (const child of product.children) {
                collectOption(child);
            }
        }
        const sortedOptions = sortProductOptions(this.filterID, [...options]);
        return sortedOptions.map((opt) => {
            return {
                id: opt,
                label: displayNameMap.get(opt) ?? opt,
            };
        });
    }

    public *applyFilter(
        selectedOptionIDs: Set<string>,
        products: Iterable<IProduct>,
    ): Generator<IProduct> {
        for (const product of products) {
            // Build a set of all of the values for the filtered attribute on this product (and it's children)
            const attrValues = new Set(
                product.children
                    .filter((child) => child.availability.is_available_to_buy)
                    .reduce<string[]>((memo, child) => {
                        const rawVal =
                            child.attributes[this.filterID]?.value || [];
                        const val = Array.isArray(rawVal)
                            ? rawVal.map((v) => `${v}`)
                            : `${rawVal}`;
                        return memo.concat(val);
                    }, []),
            );
            if (product.availability.is_available_to_buy) {
                const rawVal = product.attributes[this.filterID]?.value || [];
                const values = Array.isArray(rawVal)
                    ? rawVal.map((v) => `${v}`)
                    : [`${rawVal}`];
                for (const val of values) {
                    attrValues.add(val);
                }
            }
            // Figure out if this product matches any of the selected values
            const overlap = intersection(selectedOptionIDs, attrValues);
            // Include the product if there's any overlap OR if nothing is selected
            if (overlap.size > 0 || selectedOptionIDs.size <= 0) {
                yield product;
            }
        }
    }
}

/**
 * Product Grid Filter which filters based on the Product Class
 */
// Not exported: the only thing that constructs one is
// `buildGridFilterConfigs` below, in this file. Exporting it again
// would be an entry point nothing enters by, and knip fails the build
// on those.
class ProductClassGridFilter extends GridFilterConfig {
    private readonly labels: Map<string, string>;

    constructor(opts: IGridFilterOptions & { labels?: Map<string, string> }) {
        super(opts);
        this.labels = opts.labels || new Map();
    }

    public listFilterOptions(
        products: ReadonlyArray<IProduct>,
    ): IFilterOption[] {
        const productClassSlugs = new Set<string>();
        const collectOption = (product: IProduct) => {
            if (product.availability.is_available_to_buy) {
                productClassSlugs.add(product.product_class_slug);
            }
        };
        for (const product of products) {
            collectOption(product);
            for (const child of product.children) {
                collectOption(child);
            }
        }
        const collator = new Intl.Collator();
        const options = Array.from(productClassSlugs)
            .map((slug) => {
                return {
                    id: slug,
                    label: this.labels.get(slug) || slug.replace("-", " "),
                };
            })
            .sort((a, b) => {
                return collator.compare(a.label, b.label);
            });
        return options;
    }

    public *applyFilter(
        selectedOptionIDs: Set<string>,
        products: Iterable<IProduct>,
    ): Generator<IProduct> {
        for (const product of products) {
            // Include the product if the product class matches OR if nothing is selected
            if (
                selectedOptionIDs.has(product.product_class_slug) ||
                selectedOptionIDs.size <= 0
            ) {
                yield product;
            }
        }
    }
}

/**
 * Product Grid Filter which filters based on several predefined product price ranges
 */
export class PriceRangeGridFilter extends GridFilterConfig {
    private readonly ranges: readonly IPriceRange[];

    constructor(opts: IGridFilterOptions & { ranges: readonly IPriceRange[] }) {
        super(opts);
        this.ranges = opts.ranges;
    }

    public listFilterOptions(): IFilterOption[] {
        return this.ranges.map((range) => {
            return {
                id: range.value.id,
                label: isSingleBoundRange(range)
                    ? this.getSingleBoundRangeLabel(range)
                    : this.getDualBoundRangeLabel(range),
            };
        });
    }

    public *applyFilter(
        selectedOptionIDs: Set<string>,
        products: Iterable<IProduct>,
    ): Generator<IProduct> {
        const selectedRanges = this.ranges.filter((r) =>
            selectedOptionIDs.has(r.value.id),
        );
        for (const product of products) {
            // If either nothing is selected, or the product matches one of the selected ranges, include it.
            if (
                selectedOptionIDs.size <= 0 ||
                this.productMatchesOneOfRanges(selectedRanges, product)
            ) {
                yield product;
            }
        }
    }

    private getSingleBoundRangeLabel(range: ISingleBoundPriceRange): string {
        const params = {
            price: roundedMoney(range.value.amount),
        };
        switch (range.value.comparator) {
            case PriceComparator.LT:
            case PriceComparator.LTE:
                return interpolate(t`under %(price)s`, params);

            case PriceComparator.GT:
            case PriceComparator.GTE:
                return interpolate(t`over %(price)s`, params);
            default:
                throw new Error(
                    `Unknown comparator type: ${range.value.comparator}`,
                );
        }
    }

    private getDualBoundRangeLabel(range: IDualBoundPriceRange) {
        const params = {
            lowerPrice: roundedMoney(range.value.lower_bound.amount),
            higherPrice: roundedMoney(range.value.upper_bound.amount),
        };
        return interpolate(t`%(lowerPrice)s to %(higherPrice)s`, params);
    }

    private productMatchesOneOfRanges(
        ranges: IPriceRange[],
        product: IProduct,
    ) {
        for (const range of ranges) {
            const matches = isSingleBoundRange(range)
                ? this.productMatchesSingleBoundRange(range, product)
                : this.productMatchesDualBoundRange(range, product);
            if (matches) {
                return true;
            }
        }
        return false;
    }

    private getProductMinMaxPrice(product: IProduct) {
        const prices: number[] = [];
        if (product.price.cosmetic_excl_tax) {
            prices.push(parseFloat(product.price.cosmetic_excl_tax));
        }
        for (const child of product.children) {
            if (child.price.cosmetic_excl_tax) {
                prices.push(parseFloat(child.price.cosmetic_excl_tax));
            }
        }
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        return {
            minPrice,
            maxPrice,
        };
    }

    private productMatchesSingleBoundRange(
        range: ISingleBoundPriceRange,
        product: IProduct,
    ): boolean {
        const { minPrice, maxPrice } = this.getProductMinMaxPrice(product);
        return this.checkPriceComparator(
            minPrice,
            maxPrice,
            range.value.comparator,
            range.value.amount,
        );
    }

    private productMatchesDualBoundRange(
        range: IDualBoundPriceRange,
        product: IProduct,
    ): boolean {
        const { minPrice, maxPrice } = this.getProductMinMaxPrice(product);
        const lowerBoundMatch = this.checkPriceComparator(
            minPrice,
            maxPrice,
            range.value.lower_bound.comparator,
            range.value.lower_bound.amount,
        );
        const upperBoundMatch = this.checkPriceComparator(
            minPrice,
            maxPrice,
            range.value.upper_bound.comparator,
            range.value.upper_bound.amount,
        );
        return lowerBoundMatch && upperBoundMatch;
    }

    private checkPriceComparator(
        productMinPrice: number,
        productMaxPrice: number,
        comparator: PriceComparator,
        thresholdAmount: number,
    ): boolean {
        switch (comparator) {
            case PriceComparator.LT:
                return productMinPrice < thresholdAmount;
            case PriceComparator.LTE:
                return productMinPrice <= thresholdAmount;
            case PriceComparator.GT:
                return productMaxPrice > thresholdAmount;
            case PriceComparator.GTE:
                return productMaxPrice >= thresholdAmount;
        }
    }
}

/**
 * Product Grid Filter which filters based on a set of boolean products attribute, where each filter option is a
 * single product attribute.
 */
export class BooleanAttributeGridFilter extends GridFilterConfig {
    private readonly attributes: readonly IBooleanAttribute[];

    constructor(
        opts: IGridFilterOptions & { attributes: readonly IBooleanAttribute[] },
    ) {
        super(opts);
        this.attributes = opts.attributes;
    }

    public listFilterOptions(): IFilterOption[] {
        return this.attributes.map((attr) => {
            return {
                id: attr.attribute,
                label: attr.label,
            };
        });
    }

    public *applyFilter(
        selectedOptionIDs: Set<string>,
        products: Iterable<IProduct>,
    ): Generator<IProduct> {
        for (const product of products) {
            if (
                selectedOptionIDs.size <= 0 ||
                this.productMatchesOneOfAttribute(product, selectedOptionIDs)
            ) {
                yield product;
            }
        }
    }

    private productMatchesOneOfAttribute(
        product: IProduct,
        attributeNames: Set<string>,
    ) {
        for (const attributeName of attributeNames) {
            const values = this.getProductAttributeValues(
                product,
                attributeName,
            );
            for (const value of values) {
                if (value) {
                    return true;
                }
            }
        }
        return false;
    }

    private getProductAttributeValues(
        product: IProduct,
        attributeName: string,
    ) {
        const values = [
            product.attributes[attributeName]?.value,
            ...product.children.map(
                (child) => child.attributes[attributeName]?.value,
            ),
        ];
        return values.filter(notEmpty);
    }
}

/**
 * Product Grid Filter which filters by the product's aggregate rating,
 * treating each option as a "this many stars and up" threshold.
 *
 * The reviews sidebar's rating filter matches a review whose rating *equals*
 * the option, which works because a review's rating is a whole number. A
 * product's rating is an average (4.3), so equality would match almost
 * nothing -- hence `>=` here. That also makes the options nest: selecting 4
 * and 5 yields the same set as selecting 4, so a union of thresholds is just
 * the lowest one.
 */
export class RatingRangeGridFilter extends GridFilterConfig {
    private readonly ratings: readonly number[];

    constructor(opts: IGridFilterOptions & { ratings: readonly number[] }) {
        super(opts);
        this.ratings = opts.ratings;
    }

    public listFilterOptions(): IFilterOption[] {
        return this.ratings.map((rating) => ({
            id: `${rating}`,
            // The stars are the visible treatment, but this string is what the
            // select-one dropdown and the applied-filter summary can show, and
            // what a screen reader gets. `IntegerBlock` allows 1, so the
            // singular form is reachable -- and locales with more than two
            // plural categories need the choice made by count, not by an
            // English-shaped guess.
            label: interpolate(
                ngettext(
                    msgid`%(rating)s star and up`,
                    `%(rating)s stars and up`,
                    rating,
                ),
                { rating: `${rating}` },
            ),
            graphic: { kind: "rating", rating: rating },
        }));
    }

    public *applyFilter(
        selectedOptionIDs: Set<string>,
        products: Iterable<IProduct>,
    ): Generator<IProduct> {
        // Only the configured thresholds count. A hand-edited or stale URL can
        // carry anything, and an unconfigured value would otherwise narrow the
        // grid with no checkbox to untick and no applied-filter pill to clear
        // -- and would contradict `list_static_option_ids`, which tells the
        // publish-time validator these are the only valid ids. See
        // `DiscountGridFilter.applyFilter` on how this differs from the four
        // older filters.
        const thresholds = this.ratings.filter((rating) =>
            selectedOptionIDs.has(`${rating}`),
        );
        const minThreshold =
            thresholds.length > 0 ? Math.min(...thresholds) : null;
        for (const product of products) {
            if (
                minThreshold === null ||
                (product.rating !== null && product.rating >= minThreshold)
            ) {
                yield product;
            }
        }
    }
}

/**
 * Product Grid Filter which filters to products whose price is discounted.
 *
 * The predicate is `isProductPriceDiscounted` applied to the product-API
 * price, which is the same function the configurator and the compare tiles
 * use for the savings callout and the struck-through price.
 *
 * It is *not* currently the same thing this grid's own card shows.
 * `CardPriceContent` reads `isProductPriceDiscounted(props.price.total)` off
 * the separately-fetched `IAPIPrice`, and `CardDefaultPrice` renders at
 * `quantity={1}`, for which `CardPrice` short-circuits to `setPrice(null)` --
 * so the tile takes its plain-price branch regardless. Selecting this filter
 * can therefore return tiles with no visible sale treatment. Same predicate,
 * different input; do not read this filter as a guarantee about what the tile
 * renders until that path is reconciled.
 */
export class DiscountGridFilter extends GridFilterConfig {
    /** Mirrored server-side by `_DiscountGridFilter.OPTION_ID`. */
    public static readonly OPTION_ID = "true";

    private readonly optionLabel: string;

    constructor(opts: IGridFilterOptions & { option_label: string }) {
        super(opts);
        this.optionLabel = opts.option_label;
    }

    public listFilterOptions(): IFilterOption[] {
        // CMS-authored, so renaming the filter to "Deals" does not leave a
        // checkbox reading "On Sale". Separate from `label` because that is
        // also the group heading this option sits under, and a facet usually
        // wants the two to differ; falling back to it keeps the common case
        // one field.
        return [
            {
                id: DiscountGridFilter.OPTION_ID,
                label: this.optionLabel || this.label,
            },
        ];
    }

    public *applyFilter(
        selectedOptionIDs: Set<string>,
        products: Iterable<IProduct>,
    ): Generator<IProduct> {
        // Keyed off the option id: testing only for a non-empty selection
        // would make `?on_sale=false` -- and any other stale value -- turn the
        // filter *on*, the opposite of what the URL says.
        //
        // Note this is not what the four older filters do with an unrecognised
        // value. They test `selectedOptionIDs.size <= 0` alone, so a value
        // matching no configured option leaves them active and matching
        // nothing: `?price=bogus` empties the grid. Neither parser validates
        // URL values against `listFilterOptions()`, so both shapes are
        // reachable. Ignoring the value is the better answer for a boolean
        // filter, but the grid now has two policies; unifying them belongs in
        // the base class and changes behaviour for every site, so it is not
        // done here.
        const isActive = selectedOptionIDs.has(DiscountGridFilter.OPTION_ID);
        for (const product of products) {
            if (!isActive || this.productIsDiscounted(product)) {
                yield product;
            }
        }
    }

    private productIsDiscounted(product: IProduct): boolean {
        // Parent and children both, and only what can actually be bought --
        // the same scan the attribute and price-range filters do.
        //
        // Which of the two applies depends on the grid. A plain grid renders
        // one card per top-level product with its children nested, so a
        // parent card matches when any purchasable variant is discounted even
        // though the card shows the parent's price. A variant grid is served
        // pre-flattened by the SSR builder, so each card is a leaf and this
        // reduces to the card's own price.
        return [product, ...product.children].some(
            (variant) =>
                variant.availability.is_available_to_buy &&
                isProductPriceDiscounted(variant.price),
        );
    }
}

/**
 * The snippet's serialized filter config, as filter objects.
 *
 * Lives beside the classes it constructs rather than in the component that
 * first needed it: a grid's chrome can be rendered outside `ProductGrid` --
 * 1-800's mobile filter panel is -- and that chrome needs the same objects
 * from the same string. Two parsers would be two lists of filter types to
 * keep in step, and the `switch` is exhaustive precisely so adding a type is
 * a compile error in exactly one place.
 *
 * The `default` is what makes that true. Without it the exhaustiveness rests
 * on `noImplicitReturns`, which this repo does not set -- a new member of the
 * `GridFilter` union with no `case` here would fall through, return
 * `undefined`, and put a hole in the array for the grid to render as a
 * missing filter.
 */
export const buildGridFilterConfigs = (
    filterConfigJSON: string,
): GridFilterConfig[] =>
    parseGridFilters(filterConfigJSON).map((data): GridFilterConfig => {
        switch (data.type) {
            case "attribute_value_grid_filter":
                return new AttributeValueGridFilter(data.value);
            case "product_class_grid_filter":
                return new ProductClassGridFilter(data.value);
            case "price_range_grid_filter":
                return new PriceRangeGridFilter(data.value);
            case "boolean_attribute_grid_filter":
                return new BooleanAttributeGridFilter(data.value);
            case "rating_range_grid_filter":
                return new RatingRangeGridFilter(data.value);
            case "discount_grid_filter":
                return new DiscountGridFilter(data.value);
            default:
                return assertNever(data);
        }
    });
